// Angular imports
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

// CC-Common imports
import {
  CCMessageBoxIcon,
  IconConstants,
  Utils
} from '@therealmanage/ciranet-ui-common';
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Devextreme imports
import { DxValidationGroupComponent } from 'devextreme-angular';

// Component imports
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// service imports
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { UserService } from '../../_services/data-services/user.service';

// Model imports
import { AddContactInfoRawDto } from '../../_models/registration/add-contact-info-raw-dto';
import { CreateUserParameterDto } from '../../_models/create-user-parameter-dto';
import { OwnerRegistrationRawDto } from '../../_models/registration/owner-registration-raw-dto';
import { RegistrationEditorResultDto } from '../../_models/registration/registration-editor-result-dto';
import { RegistrationParameterDto } from '../../_models/registration/registration-parameter-dto';
import { ResultStatusDto } from '../../_models/result-status-dto';

@Component({
  selector: 'app-signup-account-name',
  templateUrl: './signup-account-name.component.html',
  styleUrls: ['./signup-account-name.component.scss'],
  standalone: false
})
export class SignupAccountNameComponent
  extends RecaptchaBaseComponent
  implements OnInit
{
  @ViewChild('validatorPortalAccountName', { static: false })
  validatorPortalAccountName: DxValidationGroupComponent;

  @Input() userInfo: CreateUserParameterDto;
  @Input() registrationEditor: RegistrationEditorResultDto;

  @Output() onPortalAccountUpdate = new EventEmitter<CreateUserParameterDto>();
  @Output() onPreviousClick = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();

  isSmallScreen = false;
  isMediumScreen = false;
  loadingVisible = false;

  owners: Array<OwnerRegistrationRawDto> = [];
  selectedOwner: OwnerRegistrationRawDto;
  selectedOwnerRaw: OwnerRegistrationRawDto;
  selectedOwnerIdentifier: string;
  userRecaptchaToken: string;

  isContactTypeVisible = false;
  isEmailCommunicationRequired = false;
  isUserCreated = false;

  registrationParameter: RegistrationParameterDto;

  isRecaptchaResolved = false;

  backToLoginIcon = IconConstants.BACK_TO_LOGIN;

  constructor(
    private screenService: ScreenService,
    private userService: UserService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService
  ) {
    super(errorService, envConfig);
  }

  ngOnInit() {
    if (!this.registrationEditor) {
      console.error('registrationEditor is required');
      this.onBack.emit(); // Navigate back gracefully
      return;
    }
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );

    this.isEmailCommunicationRequired =
      this.registrationEditor.emailCustomDataFields.length > 0 ? true : false;

    this.prepareOwners();
    this.registrationParameter = new RegistrationParameterDto();
    this.registrationParameter.emailData = new AddContactInfoRawDto();

    this.checkRecaptchaValidation();
  }

  btnPreviousClick(): void {
    this.onPreviousClick.emit();
  }

  btnNextClick(): void {
    if (this.validatorPortalAccountName.instance.validate().isValid == false) {
      return;
    }

    this.prepareUserInfo();
    this.onPortalAccountUpdate.emit(this.userInfo);
  }

  btnRegisterClick(): void {
    if (this.validatorPortalAccountName.instance.validate().isValid == false) {
      return;
    }
    if (!this.userRecaptchaToken && this.isValidRecaptcha()) {
      this.userInfo.recaptchaToken = this.recaptchaToken;
    }
    if (!!this.userInfo.recaptchaToken) {
      this.prepareUserInfo();
      // if we register here, that means the community does not support the mass communication for emails
      // we need to assign a SubTypeId for a emailData because it is required
      if (!this.userInfo.contactInfo.emailData.subTypeId) {
        this.userInfo.contactInfo.emailData.subTypeId =
          this.registrationEditor.defaultEmailSubTypeId;
      }

      this.loadingVisible = true;

      RxjsUtils.safeSubscribe(
        this.userService.createUser(this.userInfo),
        (result: ResultStatusDto) => {
          this.resetRecaptcha();
          this.loadingVisible = false;
          if (result.isSuccess == false) {
            this.messageBox.showWithFixedWidthAndIcon(
              CCMessageBoxIcon.error,
              'Error',
              `There is an error creating user. ${result.message}`,
              320
            );
          } else {
            this.isUserCreated = true;
          }
        },
        (error) => {
          this.resetRecaptcha();
          this.loadingVisible = false;
          this.processApiError(error);
        }
      );
    }
  }

  // Override
  override processApiError(error: any): void {
    const apiError = error.error;
    if (
      apiError &&
      apiError.message &&
      apiError.message.indexOf('captcha') !== -1
    ) {
      this.userRecaptchaToken = null;
      this.userInfo.recaptchaToken = null;
      this.messageBox.showErrorMessage(
        'Invalid Token',
        this.validationErrorMessage
      );
      this.checkRecaptchaValidation();
    } else {
      this.errorService.displayError(error);
    }
  }

  onOwnerSelection(): void {
    this.selectedOwner = this.owners.find(
      (d) => d.ownerIdentifier === this.selectedOwnerIdentifier
    );

    if (!this.selectedOwner) {
      console.error('Selected owner not found');
      return;
    }

    this.selectedOwnerRaw = Object.assign(
      this.selectedOwnerRaw,
      this.selectedOwner
    );

    this.registrationParameter.emailData.contactId =
      this.selectedOwner.contactId;
    this.registrationParameter.emailData.contactTypeId =
      this.selectedOwner.contactTypeId;
    this.prepareContactType();
  }

  onContactTypeValueChanged(): void {
    this.registrationParameter.emailData.contactTypeId =
      this.selectedOwnerRaw.contactTypeId;
  }

  isOwnerNameTextFieldsReadOnly(): boolean {
    // for 'Other' owner, the textbox is editable. for 'owner 1' or 'owner 2', we want to keep the current owner name.
    return (
      !this.selectedOwnerIdentifier ||
      this.selectedOwnerIdentifier.toUpperCase() === 'OWNER_1' ||
      this.selectedOwnerIdentifier.toUpperCase() === 'OWNER_2'
    );
  }

  getPlaceHolderForMiddleName(): string {
    if (this.isOwnerNameTextFieldsReadOnly()) {
      return '';
    }
    return 'Middle Name';
  }

  onAccountRecaptchaResolved(event: any): void {
    this.onRecaptchaResolved(event);
    this.checkRecaptchaValidation();
  }

  private checkRecaptchaValidation(): void {
    this.isRecaptchaResolved =
      this.isValidRecaptcha() || Utils.notNullOrEmpty(this.userRecaptchaToken);
  }

  private prepareUserInfo(): void {
    this.userInfo.contactInfo = this.registrationParameter;
    this.userInfo.contactInfo.owner = this.selectedOwnerRaw;
    this.userInfo.contactInfo.emailData.content = this.userInfo.email;
  }

  private prepareContactType(): void {
    this.isContactTypeVisible =
      this.selectedOwner.firstName === 'Other' ? true : false;
    if (!!this.isContactTypeVisible) {
      this.selectedOwnerRaw.firstName = null;
      this.selectedOwnerRaw.contactTypeId = null;
    }
  }

  private prepareOwners(): void {
    this.owners = this.registrationEditor.owners;
    this.selectedOwner = new OwnerRegistrationRawDto();
    this.selectedOwnerRaw = new OwnerRegistrationRawDto();
    this.owners.forEach((owner) => {
      if (!owner.firstName) {
        owner.firstName = 'Other';
      }
    });

    if (!!this.userInfo) {
      this.userRecaptchaToken = this.userInfo.recaptchaToken;
      if (!!this.userInfo.contactInfo) {
        this.registrationParameter = this.userInfo.contactInfo;

        if (!!this.userInfo.contactInfo.owner) {
          this.selectedOwnerRaw = this.userInfo.contactInfo.owner;
          this.selectedOwner = this.owners.find(
            (d) => d.ownerIdentifier === this.selectedOwnerRaw.ownerIdentifier
          );
          if (this.selectedOwner) {
            this.selectedOwnerIdentifier = this.selectedOwner.ownerIdentifier;
            this.isContactTypeVisible =
              this.selectedOwner.firstName === 'Other';
          }
        }
      }
    }
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    this.isMediumScreen = this.screenService.isMediumScreen();
  }
}
